<template>
  <div v-if="task != null">
    <v-alert v-if="!pd" type="warning" variant="tonal" density="compact">
      La tarea no tiene línea de picking asociada.
    </v-alert>

    <template v-else>
      <!-- Orden de expedicion -->
      <h6 class="mb-2">Orden de expedición</h6>
      <v-row class="mb-2">
        <v-col cols="12" md="3" v-if="so">
          <div class="text-caption text-medium-emphasis">Código</div>
          <div>{{ so.code }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="so && so.state">
          <div class="text-caption text-medium-emphasis">Estado OE</div>
          <v-chip size="small">{{ so.state.name }}</v-chip>
        </v-col>
        <v-col cols="12" md="3" v-if="so && so.client">
          <div class="text-caption text-medium-emphasis">Cliente</div>
          <div>{{ so.client.name }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="so">
          <div class="text-caption text-medium-emphasis">Ola</div>
          <div>{{ so.wave ? "#" + so.wave.id : "-" }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="soi">
          <div class="text-caption text-medium-emphasis">Ítem OE</div>
          <div>#{{ soi.id }} <span v-if="soi.state" class="text-caption text-grey-600">- {{ soi.state.name }}</span></div>
        </v-col>
      </v-row>

      <!-- Linea de picking -->
      <h6 class="mb-2">Línea de picking #{{ pd.id }}</h6>
      <v-row class="mb-2">
        <v-col cols="12" md="3" v-if="pd.status">
          <div class="text-caption text-medium-emphasis">Estado línea</div>
          <v-chip size="small">{{ pd.status.name }}</v-chip>
        </v-col>
        <v-col cols="12" md="3" v-if="soi && soi.product_presentation">
          <div class="text-caption text-medium-emphasis">Producto</div>
          <div>{{ soi.product_presentation.product.name }}</div>
          <span class="text-caption text-grey-600">SKU: {{ soi.product_presentation.product.sku }}</span>
        </v-col>
        <v-col cols="12" md="3">
          <div class="text-caption text-medium-emphasis">A pickear</div>
          <div>
            {{ pd.amount_to_pick }}
            <span v-if="pt" class="text-caption text-grey-600">{{ getPresentationTypeName(pd.amount_to_pick, pt.id, pt.name) }}</span>
          </div>
        </v-col>
        <v-col cols="12" md="3" v-if="pd.storage_units_amount_to_pick != null">
          <div class="text-caption text-medium-emphasis">Almacenamiento a pickear</div>
          <div>{{ pd.storage_units_amount_to_pick }}</div>
        </v-col>

        <v-col cols="12" md="3" v-if="task.amount_picked > 0">
          <div class="text-caption text-medium-emphasis">Pickeado</div>
          <div>{{ task.amount_picked }} <span v-if="task.storage_units_amount_picked != null" class="text-caption text-grey-600">/ {{ task.storage_units_amount_picked }} alm.</span></div>
        </v-col>
        <v-col cols="12" md="3" v-if="task.has_incidencia">
          <div class="text-caption text-medium-emphasis">Incidencia</div>
          <v-chip size="small" color="error">Con incidencia</v-chip>
        </v-col>

        <v-col cols="12" md="3" v-if="pd.lpn">
          <div class="text-caption text-medium-emphasis">LPN sugerido</div>
          <div>{{ pd.lpn.code }}</div>
        </v-col>
      </v-row>

      <div class="d-flex align-center gap-3 mb-3" v-if="(pd.lpn_picked_origin || pd.ubicacion_picked_origin) || (pd.lpn_picked_destination || pd.ubicacion_picked_destination)">
        <div v-if="pd.lpn_picked_origin || pd.ubicacion_picked_origin" class="d-flex flex-column">
          <span class="font-weight-bold text-caption">Origen real</span>
          <span v-if="pd.lpn_picked_origin">{{ pd.lpn_picked_origin.code }}</span>
          <span v-if="pd.ubicacion_picked_origin" class="text-caption text-grey-600">{{ pd.ubicacion_picked_origin.code }}</span>
        </div>
        <v-divider v-if="(pd.lpn_picked_origin || pd.ubicacion_picked_origin) && (pd.lpn_picked_destination || pd.ubicacion_picked_destination)" vertical class="mx-2"></v-divider>
        <div v-if="pd.lpn_picked_destination || pd.ubicacion_picked_destination" class="d-flex flex-column">
          <span class="font-weight-bold text-caption">Destino real</span>
          <span v-if="pd.lpn_picked_destination">{{ pd.lpn_picked_destination.code }}</span>
          <span v-if="pd.ubicacion_picked_destination" class="text-caption text-grey-600">{{ pd.ubicacion_picked_destination.code }}</span>
        </div>
      </div>

      <div class="mb-3">
        <v-chip v-if="pd.stock_reserves && pd.stock_reserves.length" size="small" class="mr-2">
          {{ pd.stock_reserves.length }} reserva(s) de stock
        </v-chip>
        <v-chip v-if="pd.picking_detals_childs && pd.picking_detals_childs.length" size="small">
          {{ pd.picking_detals_childs.length }} sub-línea(s)
        </v-chip>
      </div>

      <!-- Atributos solicitados -->
      <h6 class="mb-2" v-if="soi">Atributos solicitados</h6>
      <v-row v-if="soi">
        <v-col cols="12" md="3" v-if="soi.lote">
          <div class="text-caption text-medium-emphasis">Lote</div>
          <div>{{ soi.lote }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="soi.serie">
          <div class="text-caption text-medium-emphasis">Serie</div>
          <div>{{ soi.serie }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="soi.expiration_date">
          <div class="text-caption text-medium-emphasis">Vencimiento</div>
          <div>{{ soi.expiration_date }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="soi.manufacture_date">
          <div class="text-caption text-medium-emphasis">Fabricación</div>
          <div>{{ soi.manufacture_date }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="soi.storage_units_amount">
          <div class="text-caption text-medium-emphasis">Almacenamiento</div>
          <div>{{ soi.storage_units_amount }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="soi.quality">
          <div class="text-caption text-medium-emphasis">Calidad</div>
          <v-chip size="small" color="primary">{{ soi.quality.name }}</v-chip>
        </v-col>
        <v-col cols="12" md="3" v-if="soi.product_status">
          <div class="text-caption text-medium-emphasis">Estado del producto</div>
          <v-chip size="small" :color="getStatusColor(soi.product_status.id)">{{ soi.product_status.name }}</v-chip>
        </v-col>
        <template v-for="d in descriptivos" :key="d.key">
          <v-col cols="12" md="3" v-if="soi[d.key]">
            <div class="text-caption text-medium-emphasis">{{ d.label }}</div>
            <div>{{ soi[d.key] }}</div>
          </v-col>
        </template>
      </v-row>
    </template>
  </div>
</template>

<script>
import { productoService } from "@/libs/ws/productoService";
import GenericMixin from "@/mixins/GenericMixin.vue";

export default {
  name: "TaskViewDetallePreparacion",
  mixins: [GenericMixin],
  props: {
    task: Object,
    descriptivos: { type: Array, default: () => [] },
  },
  computed: {
    pd() {
      return this.task ? this.task.picking_detail : null;
    },
    soi() {
      return this.pd ? this.pd.shipment_order_item : null;
    },
    so() {
      return this.soi ? this.soi.shipment_order : null;
    },
    pt() {
      return this.soi && this.soi.product_presentation ? this.soi.product_presentation.presentation_type : null;
    },
  },
  methods: {
    getStatusColor(status) {
      return productoService.getStatusColor(status);
    },
  },
};
</script>

<style scoped>
.gap-3 > * + * {
  margin-left: 12px;
}
</style>
